import { channelInfo } from '@op/components/ChannelLabel';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { chartAllColors } from '@op/constants';
import { getPercentageValue, sortMapAuxiliaryFunc } from '@op/utils';
import { getChartBufferWidth } from '@op/utils/systemUtil';
import { formatTimeToShow } from '@op/utils/time';
import { percentageFormat, reviewsFormat, yAxleFormat } from '@op/utils/value';
import React from 'react';
import { useMemo } from 'react';
import { useIntl } from 'umi';

export default function HBarChart({
  filter,
  data,
  height = 330,
  title,
  sortMap,
  colorMap = {},
  translate = (value: string) => value,
}: any) {
  const { formatMessage } = useIntl();
  const options = useMemo(() => {
    const info: { [key: string]: any } = {};
    const keys = new Set<string>();
    const legendData: Array<any> = [];
    const totalMap: { [key: string]: number } = {};
    data.forEach((item: { channel: string; name: any; value: number }) => {
      const { channel, name, value } = item;
      if (!info[channel]) info[channel] = {};
      keys.add(name);
      info[channel][name] = value || 0;
      totalMap[channel] = (totalMap[channel] || 0) + (value || 0);
    });

    const yAxis = Object.keys(totalMap);
    yAxis.sort(sortMapAuxiliaryFunc(totalMap, (item) => item));
    const series = Array.from(keys).map((key: string, index: number) => {
      const color = colorMap[key] || chartAllColors[index];
      const listData = yAxis.map((channel) => {
        const detail: any = info[channel] || {};
        return detail[key] || 0;
      });

      legendData.push({
        name: key,
        icon: 'circle',
        itemStyle: {
          color,
        },
      });
      return {
        name: key,
        type: 'bar',
        stack: 'total',
        data: listData,
        itemStyle: {
          color,
        },
      };
    });
    if (sortMap) {
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      series.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
    }
    return {
      legend: {
        data: legendData,
        formatter: translate,
      },
      tooltip: {
        formatter: (params: any) => {
          const time = formatTimeToShow(filter);
          return formatChartParamsDate({
            title: channelInfo(params?.[0]?.axisValueLabel as any).name,
            params,
            dealwithRecords(records, total) {
              records.sort(
                (a, b) => (b.originValue || 0) - (a.originValue || 0),
              );

              records = records.map((item) => {
                return {
                  ...item,
                  value: `${item.value}(${percentageFormat(
                    getPercentageValue(item?.originValue || 0, total, 6) * 100,
                  )})`,
                };
              });

              records.unshift({
                text: formatMessage({ id: 'Total' }),
                value: reviewsFormat(total),
              });

              return records;
            },
            listTitleFunc: (item) => translate(item.seriesName),
            valueFormat: (value) => reviewsFormat(value),
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    weight: 'bold',
                    header: time,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: title,
                  },
                ],
              },
            ],
          });
        },
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          align: 'center',
          formatter: (value: number) => yAxleFormat(value),
        },
      },
      yAxis: {
        type: 'category',
        data: yAxis,
        splitLine: {
          show: false,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          formatter: (value: string) => channelInfo(value as any).name,
        },
      },
      grid: {
        left: getChartBufferWidth(6),
      },
      series: series,
    };
  }, [data, translate, title]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar' }}
      options={options}
      height={height}
      empty={data?.length === 0}
    ></EChartCommon>
  );
}
